// Hero.jsx
// ─ Bright outdoor adventure style
// ─ More vibrant / fun / sunny feel

import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiArrowRight } from 'react-icons/fi';

/* ─────────────────────────────────────────────────────────
   IMAGE MAP
───────────────────────────────────────────────────────── */
const IMAGES = {
  heroBg: '/images/buggy.JPG',
  buggy: '/images/buggyteam2.jpeg',
  chainsaw: '/images/chainsaw.png',
  naan: '/images/naan.png',
  harvest: '/images/trampoline.png',
  restaurant: '/images/paprika.jpg',
  logo: '/images/logo.JPG',
};

/* ─────────────────────────────────────────────────────────
   MOTION VARIANTS
───────────────────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 48 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.4, 0, 0.2, 1],
      delay,
    },
  }),
};

const slideIn = {
  hidden: { opacity: 0, x: 60 },
  visible: (delay = 0) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease: [0.4, 0, 0.2, 1],
      delay,
    },
  }),
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const NAV_H = 70;

/* ─────────────────────────────────────────────────────────
   FEATURED TILE
───────────────────────────────────────────────────────── */
function FeaturedTile({ lang }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      className="relative overflow-hidden cursor-pointer flex flex-col justify-end"
      style={{
        flex: '1.6',
        borderBottom: '1px solid rgba(200,52,10,0.14)',
        minHeight: 200,
      }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      {/* IMAGE */}
      <motion.img
        src={IMAGES.buggy}
        alt="Buggy off-road riding experience"
        className="absolute inset-0 w-full h-full object-cover"
        animate={{
          scale: hovered ? 1.06 : 1,
          filter: hovered
            ? 'brightness(0.92) saturate(1.15)'
            : 'brightness(0.78) saturate(1.05)',
        }}
        transition={{
          duration: 0.55,
          ease: 'easeOut',
        }}
      />

      {/* OVERLAYS */}
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,16,8,0.78)] via-[rgba(8,16,8,0.22)] to-[rgba(8,16,8,0.02)]" />

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,200,110,0.12),transparent_45%)]" />

      {/* CONTENT */}
      <div className="relative z-10 p-7 pb-6">
        <div
          className="
            inline-flex items-center gap-1.5 mb-3 w-fit
            bg-[rgba(200,52,10,0.22)]
            border border-[rgba(255,170,120,0.35)]
            rounded-full px-3 py-1
            text-[9px] font-bold tracking-[0.2em]
            text-[#ffd4c4] uppercase
          "
        >
          ★ {lang === 'ja' ? '看板体験' : 'Signature'}
        </div>

        <p className="text-[10px] font-bold tracking-[0.3em] text-white/50 mb-2 uppercase">
          01 / FLAGSHIP
        </p>

        <p className="font-notoSerif text-white text-[22px] leading-tight tracking-wide">
          {lang === 'ja' ? 'バギー乗車体験' : 'Buggy Riding'}
        </p>

        <p className="mt-1.5 text-[9px] font-bold tracking-[0.22em] text-white/55 uppercase">
          {lang === 'ja'
            ? 'Off-Road · 全レベル対応'
            : 'Off-Road · All Levels'}
        </p>
      </div>

      {/* ARROW */}
      <motion.div
        className="
          absolute bottom-5 right-5 z-10
          w-6 h-6 rounded-full
          border border-[rgba(255,190,120,0.5)]
          flex items-center justify-center
          text-[#ffd7aa] text-[10px]
          bg-white/[0.05]
        "
        animate={{
          opacity: hovered ? 1 : 0,
        }}
        transition={{ duration: 0.2 }}
      >
        →
      </motion.div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────
   SMALL TILE
───────────────────────────────────────────────────────── */
function SmallTile({
  id,
  image,
  ja,
  en,
  subJa,
  subEn,
  lang,
  noBorderRight = false,
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      className="relative overflow-hidden cursor-pointer flex flex-col justify-end p-5"
      style={{
        flex: 1,
        minHeight: 110,
        ...(noBorderRight
          ? {}
          : {
              borderRight: '1px solid rgba(255,255,255,0.05)',
            }),
      }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      {/* IMAGE */}
      <motion.img
        src={image}
        alt={en}
        className="absolute inset-0 w-full h-full object-cover"
        animate={{
          scale: hovered ? 1.08 : 1,
          filter: hovered
            ? 'brightness(0.95) saturate(1.12)'
            : 'brightness(0.8) saturate(1.02)',
        }}
        transition={{
          duration: 0.5,
          ease: 'easeOut',
        }}
      />

      {/* OVERLAY */}
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,16,8,0.68)] via-[rgba(8,16,8,0.12)] to-transparent" />

      {/* CONTENT */}
      <div className="relative z-10">
        <p className="text-[9px] font-bold tracking-[0.3em] text-white/35 mb-1">
          {id}
        </p>

        <p className="font-notoSerif text-[13.5px] text-white leading-snug tracking-wide">
          {lang === 'ja' ? ja : en}
        </p>

        <p className="mt-1 text-[8px] font-bold tracking-[0.18em] text-white/55 uppercase">
          {lang === 'ja' ? subJa : subEn}
        </p>
      </div>

      {/* ARROW */}
      <motion.div
        className="
          absolute bottom-4 right-4 z-10
          w-5 h-5 rounded-full
          border border-[rgba(255,190,120,0.45)]
          flex items-center justify-center
          text-[#ffd7aa] text-[9px]
          bg-white/[0.04]
        "
        animate={{
          opacity: hovered ? 1 : 0,
        }}
        transition={{
          duration: 0.2,
        }}
      >
        →
      </motion.div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN HERO
───────────────────────────────────────────────────────── */
export default function Hero() {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [ready, setReady] = useState(false);

  const sectionRef = useRef(null);

  const { scrollY } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const bgY = useTransform(scrollY, [0, 700], ['0%', '12%']);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
    });

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative overflow-hidden bg-[#081108] font-syne"
      style={{
        paddingTop: NAV_H,
      }}
    >
      {/* ═══════════════════════════════════════
          PARALLAX BACKGROUND
      ═══════════════════════════════════════ */}
      <motion.div
        className="absolute inset-0 z-0 scale-110"
        style={{
          y: bgY,
          top: -NAV_H,
        }}
      >
        <img
          src={IMAGES.heroBg}
          alt="Outdoor adventure in Japan"
          className="
            w-full h-full object-cover object-[center_40%]
            brightness-[1.08] saturate-[1.15]
          "
          loading="eager"
          fetchPriority="high"
        />

        {/* MAIN OVERLAYS */}
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(6,20,8,0.58)] via-[rgba(6,20,8,0.28)] to-[rgba(6,20,8,0.08)]" />

        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(5,12,5,0.72)] via-[rgba(5,12,5,0.18)] to-transparent" />

        {/* SUNLIGHT */}
        <div className="absolute top-0 right-0 w-[45%] h-[45%] bg-[radial-gradient(circle,rgba(255,210,120,0.18)_0%,transparent_72%)]" />
        <div className="absolute top-0 left-0 right-0 h-[120px] bg-gradient-to-b from-white/60 to-transparent" />

      </motion.div>


      {/* ═══════════════════════════════════════
          AMBIENT FX
      ═══════════════════════════════════════ */}
      <div className="pointer-events-none absolute inset-0 z-[1]">
        <div className="absolute -top-48 -left-24 w-[640px] h-[640px] rounded-full bg-[radial-gradient(circle,rgba(255,190,100,0.08)_0%,transparent_70%)]" />

        <div className="absolute bottom-0 right-72 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(255,160,80,0.1)_0%,transparent_70%)]" />

        {/* EXTRA SUN GLOW */}
        <div className="absolute top-[-120px] right-[-120px] w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(255,190,90,0.14)_0%,transparent_72%)]" />

        {/* GRID */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.012) 1px,transparent 1px),' +
              'linear-gradient(90deg,rgba(255,255,255,0.012) 1px,transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* SCAN LINE */}
        <motion.div
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(255,220,160,0.18)] to-transparent"
          animate={{
            top: ['-1%', '101%'],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      </div>

      {/* ═══════════════════════════════════════
          HERO STAGE
      ═══════════════════════════════════════ */}
      <div
        className="relative z-20 grid grid-cols-1 lg:grid-cols-[1fr_400px]"
        style={{
          minHeight: `calc(100vh - ${NAV_H}px)`,
        }}
      >
        {/* ══ LEFT CONTENT ══ */}
        <motion.div
          className="flex flex-col justify-end px-8 pb-20 pt-12 sm:px-12 lg:px-14"
          initial="hidden"
          animate={ready ? 'visible' : 'hidden'}
          variants={stagger}
        >
          {/* LOCATION */}
         {/* LOGO + LOCATION */}
<motion.div
  variants={fadeUp}
  custom={0}
  className="flex items-center gap-5 mb-8"
>
  <img
    src={IMAGES.logo}
    alt="掛川バギー＆アウトドアパーク"
    className="h-12 w-auto object-contain rounded-xl shrink-0"
    style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' }}
  />
  <div className="flex items-center gap-3">
    <div className="w-8 h-px bg-[#ffd18a] shrink-0" />
    <span className="text-[10px] font-bold tracking-[0.35em] text-[#ffd18a] uppercase">
      Shizuoka · Kakegawa
    </span>
  </div>
</motion.div>

          {/* HEADLINE */}
          <motion.h1
            variants={fadeUp}
            custom={0.1}
            className="leading-none mb-5"
          >
            <span
              className="
                block font-cormorant font-bold
                text-white tracking-[-0.02em]
                leading-[0.88]
              "
              style={{
                fontSize: 'clamp(50px,8vw,108px)',
              }}
            >
              {lang === 'ja' ? '掛川バギー' : 'Kakegawa Buggy '}
            </span>

            <span
              className="
                block font-cormorant font-light italic
                text-[#ffd18a]
                tracking-[0.02em]
                leading-[0.88]
              "
              style={{
                fontSize: 'clamp(52px,7vw,94px)',
              }}
            >
              {lang === 'ja' ? '& アウトドアパーク' : '& Outdoor Park'}
            </span>

            <span
              className="
                block font-notoSerif font-light
                text-white/55 tracking-[0.28em]
                mt-4
              "
              style={{
                fontSize: 'clamp(13px,1.6vw,19px)',
              }}
            >
              {lang === 'ja'
                ? '掛川市 · アウトドアアドベンチャー'
                : '掛川市 · Outdoor Experience · Japan'}
            </span>
          </motion.h1>

          {/* INFO */}
          <motion.div
            variants={fadeUp}
            custom={0.2}
            className="flex items-center gap-5 mb-14 flex-wrap"
          >
            {[
              {
                label: lang === 'ja' ? '体験' : 'Activities',
                value: '6 Experiences',
              },
              {
                label: lang === 'ja' ? '英語対応' : 'Language',
                value: 'EN · JP · 日英OK',
              },
              {
                label: lang === 'ja' ? '営業' : 'Open',
                value: lang === 'ja' ? '年中無休' : 'Year-Round',
              },
            ].map((d, i) => (
              <React.Fragment key={d.label}>
                {i > 0 && (
                  <div className="w-px h-9 bg-white/15 shrink-0" />
                )}

                <div>
                  <p className="text-[9px] font-bold tracking-[0.3em] text-white/45 uppercase mb-1">
                    {d.label}
                  </p>

                  <p className="text-[13px] font-semibold tracking-[0.04em] text-white/90">
                    {d.value}
                  </p>
                </div>
              </React.Fragment>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            variants={fadeUp}
            custom={0.3}
            className="flex items-center gap-5 flex-wrap"
          >
            <motion.button
              onClick={() => scrollTo('reservation')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="
                group relative flex items-center gap-3.5 overflow-hidden
                bg-[#ff6b2c] hover:bg-[#f1581b]
                text-white text-[11.5px]
                font-bold tracking-[0.18em] uppercase
                px-7 py-4 rounded-[6px]
                border-0 cursor-pointer
                transition-colors duration-300
                shadow-[0_12px_40px_rgba(255,110,40,0.25)]
              "
            >
              <span className="absolute inset-0 bg-gradient-to-br from-white/[0.12] to-transparent pointer-events-none" />

              <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 group-hover:bg-white/25 transition-colors">
                <FiArrowRight size={13} />
              </span>

              {lang === 'ja'
                ? '今すぐ予約する'
                : 'Reserve Your Day'}
            </motion.button>

            <motion.button
              onClick={() => scrollTo('activities')}
              className="
                group flex items-center gap-2.5
                bg-transparent border-0 cursor-pointer
                text-[10.5px] font-bold
                tracking-[0.2em] uppercase
                text-white/60 hover:text-white
                transition-colors duration-300
              "
              whileHover="hover"
              initial="rest"
            >
              <motion.span
                className="inline-block h-px bg-current"
                variants={{
                  rest: { width: 24 },
                  hover: {
                    width: 40,
                    transition: { duration: 0.3 },
                  },
                }}
              />

              {lang === 'ja'
                ? 'ツアーを見る'
                : 'View All Tours'}
            </motion.button>
          </motion.div>
        </motion.div>

        {/* ══ RIGHT PANEL ══ */}
        <motion.div
          className="
            hidden lg:flex flex-col
            bg-[rgba(18,28,18,0.48)]
            backdrop-blur-md
            border-l border-white/[0.06]
            overflow-hidden relative
          "
          variants={slideIn}
          initial="hidden"
          animate={ready ? 'visible' : 'hidden'}
          custom={0.15}
        >
          {/* WATERMARK */}
          <div className="pointer-events-none select-none absolute -top-5 -right-2 font-cormorant font-bold text-[260px] leading-none text-[rgba(255,220,160,0.04)] z-0">
            6
          </div>

          {/* HEADER */}
          <div
            className="relative z-10 shrink-0 flex items-end px-7 pb-3"
            style={{
              height: 52,
            }}
          >
            <p className="text-[9px] font-bold tracking-[0.35em] text-white/30 uppercase">
              Activities
            </p>

            <div className="absolute bottom-0 left-0 right-0 h-px bg-white/[0.05]" />
          </div>

          {/* FEATURED */}
          <FeaturedTile lang={lang} />

          {/* ROW 1 */}
          <div
            className="grid grid-cols-2 flex-1 border-b border-white/[0.04]"
            style={{ minHeight: 0 }}
          >
            <SmallTile
              id="02"
              image={IMAGES.chainsaw}
              lang={lang}
              ja="チェンソー"
              en="Chainsaw Art"
              subJa="Chainsaw Art"
              subEn="チェンソーアート"
            />

            <SmallTile
              id="03"
              image={IMAGES.naan}
              lang={lang}
              ja="ナン焼き"
              en="Naan Baking"
              subJa="Naan Baking"
              subEn="ナン焼き体験"
              noBorderRight
            />
          </div>

          {/* ROW 2 */}
          <div
            className="grid grid-cols-2 flex-1"
            style={{ minHeight: 0 }}
          >
            <SmallTile
              id="04"
              image={IMAGES.harvest}
              lang={lang}
              ja="トランポリン"
              en="Trampoline"
              subJa="Jump · Fun"
              subEn="トランポリン"
            />

            <SmallTile
              id="05·06"
              image={IMAGES.restaurant}
              lang={lang}
              ja="レストラン"
              en="Restaurant"
              subJa="Nepal Restaurant"
              subEn="ネパール料理"
              noBorderRight
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}